import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import Markdown from 'marked-react';
import { Link, useParams } from 'react-router-dom';
import { Seo } from '../components/site/Seo';
import { useContentfulCollection } from '../hooks/useContentfulCollection';
import { NotFoundPage } from './NotFoundPage';

const richTextOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    [MARKS.CODE]: (text) => <code>{text}</code>,
    [MARKS.STRIKETHROUGH]: (text) => <s>{text}</s>,
    [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
    [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
    [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
    [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
    [BLOCKS.HEADING_5]: (node, children) => <h5>{children}</h5>,
    [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [BLOCKS.UL_LIST]: (node, children) => <ul className='bullet-list'>{children}</ul>,
    [BLOCKS.OL_LIST]: (node, children) => <ol className='bullet-list'>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    [BLOCKS.QUOTE]: (node, children) => <blockquote className='detail-quote'>{children}</blockquote>,
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.TABLE]: (node, children) => (
      <div className='rich-text-table-wrapper'>
        <table><tbody>{children}</tbody></table>
      </div>
    ),
    [BLOCKS.TABLE_ROW]: (node, children) => <tr>{children}</tr>,
    [BLOCKS.TABLE_HEADER_CELL]: (node, children) => <th>{children}</th>,
    [BLOCKS.TABLE_CELL]: (node, children) => <td>{children}</td>,
    [INLINES.HYPERLINK]: (node, children) => (
      <a href={node.data.uri} target='_blank' rel='noopener noreferrer'>
        {children}
      </a>
    ),
  },
};

function formatCaseStudyDate(value) {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getCaseStudyImage(study) {
  return study.featuredImage || study.heroImage || '';
}

function getCaseStudySummary(study) {
  return study.summary || '';
}

function getCaseStudyMeta(study) {
  const keywordLabel = study.keywords?.length ? study.keywords.slice(0, 2).join(' · ') : '';
  const date = formatCaseStudyDate(study.date || study.publishedAt);

  return [keywordLabel || study.sector, date].filter(Boolean).join(' · ');
}

export function CaseStudyDetailPage() {
  const { slug } = useParams();
  const { items: caseStudies, isLoading } = useContentfulCollection('caseStudies', { pageSize: 50 });
  const study = caseStudies.find((item) => item.slug === slug);

  if (!study && isLoading) {
    return <div className='page-shell loading-shell'>Loading case study…</div>;
  }

  if (!study) {
    return <NotFoundPage />;
  }

  const image = getCaseStudyImage(study);
  const summary = getCaseStudySummary(study);
  const meta = getCaseStudyMeta(study);
  const hasStructuredFallback = study.challenge || study.solution || study.outcomes?.length;

  return (
    <>
      <Seo title={`${study.title} | Case studies | Rivelin Robotics`} description={summary} image={image} />
      <article className='detail-shell'>
        <Link className='inline-link back-link' to='/case-studies'>
          ← Back to case studies
        </Link>
        {meta ? <p className='section-label'>{meta}</p> : null}
        <h1 className='detail-title'>{study.title}</h1>
        {summary ? <p className='detail-summary'>{summary}</p> : null}
        {image ? <img src={image} alt={study.title} className='detail-hero-image' loading='lazy' decoding='async' /> : null}
        {study.bodyRichText ? (
          <div className='detail-body markdown-body rich-text-body'>
            {documentToReactComponents(study.bodyRichText, richTextOptions)}
          </div>
        ) : study.body ? (
          <div className='detail-body markdown-body'>
            <Markdown>{study.body}</Markdown>
          </div>
        ) : null}
        {!study.body && hasStructuredFallback ? (
          <div style={{ display: 'grid', gap: '1.5rem', marginTop: '2rem' }}>
            {study.challenge ? (
              <section className='detail-panel'>
                <h2>Challenge</h2>
                <p>{study.challenge}</p>
              </section>
            ) : null}
            {study.solution ? (
              <section className='detail-panel'>
                <h2>Solution</h2>
                <p>{study.solution}</p>
              </section>
            ) : null}
            {study.outcomes?.length ? (
              <section className='detail-panel'>
                <h2>Operational outcomes</h2>
                <ul className='bullet-list'>
                  {study.outcomes.map((outcome) => (
                    <li key={outcome}>{outcome}</li>
                  ))}
                </ul>
              </section>
            ) : null}
            {study.quote?.text ? (
              <blockquote className='detail-quote'>
                <p>{study.quote.text}</p>
                <footer>{study.quote.attribution}</footer>
              </blockquote>
            ) : null}
          </div>
        ) : null}
        <div className='hero-actions' style={{ justifyContent: 'center', marginTop: 'clamp(2rem, 4vw, 3.5rem)' }}>
          <Link className='primary-cta' to='/request-demo'>Book a Demo</Link>
        </div>
      </article>
    </>
  );
}
